<script setup lang="ts">
import { HgLayout, HgMainNavItem, HgSideMenuNavItem, MenuItem } from '@smg-real-estate/domus';
import { useExperimentsStore } from '@smg-real-estate/openfeature-vue';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { useHead } from '@unhead/vue';
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useAuth0Client } from '@/composables/useAuth0Client';
import { useExperimentConsentRefresh } from '@/composables/useExperimentConsentRefresh';
import { useRouting } from '@/composables/useRouting';
import { checkUserAuthenticated } from '@/helpers/Auth/authClient';
import { getAppMetaInfo } from '@/helpers/Meta/metaInfoHelper';
import { getPage, isFunnelPage } from '@/helpers/Route/routeHelper';
import { useCommonStore } from '@/stores/common/common';
import { useAppTracking } from '@/tracking/tracking';
import { Pages } from '@/types';
import {
  getAndroidWebViewParamsFromStorage,
  setAndroidWebViewParamsInStorage,
} from './helpers/SessionStorage/sessionStorageHelper';
import { SplitFeatureFlags } from './splitExperiments/splitFeatureFlags';

// Composables
const { tm, locale } = useI18n();
const route = useRoute();
const commonStore = useCommonStore();
const experimentsStore = useExperimentsStore();
const auth0 = useAuth0Client();
useExperimentConsentRefresh();

// Context and reactive data
const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;
const accountBaseUrl = import.meta.env.VUE_APP_ACCOUNT_VIEW_BASE_URL as string;
const source = route.query?.source as string;
const tenantPlusCh = route.query?.tenantPlusCh as string;
const isLoggedIn = ref(false);
const { getLanguageSwitchLink } = useRouting();
const {
  handleMainNavTracking,
  handleMobileMenuNavTracking,
  handleTrackFooterClick,
  handleTrackHeaderClick,
} = useAppTracking(locale);
const ANDROID_WEBVIEW_SOURCE = 'ANDROID_WEBVIEW';

// Computed properties (feature flags)
const isNewNavigationEnabled = computed(() =>
  experimentsStore.isTreatmentOn(SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);

// Computed properties (component logic)
const adMenuItem = computed((): MenuItem => {
  return tm(
    isNewNavigationEnabled.value
      ? '$hg.nav.topNavNewListingMenuItem'
      : '$hg.nav.harmonize.newListingMenuItem',
  ) as unknown as MenuItem;
});

const favouritesMenuItem = computed((): MenuItem => {
  return tm('$hg.nav.favoriteMenuItem') as unknown as MenuItem;
});

const userAccountMenuItem = computed((): MenuItem => {
  return {
    label: tm('$hg.nav.harmonize.overviewMenuItem.label'),
    href: `${accountBaseUrl}/${locale.value}/overview`,
  };
});

const mainHeaderMenuItem = computed((): MenuItem => {
  if (isLoggedIn.value) {
    let privateItems = JSON.parse(JSON.stringify(tm('$hg.nav.privateItems')));
    if (Array.isArray(privateItems)) {
      privateItems = privateItems.map((item) => {
        let modifiedHref;

        if (
          item.href.includes('messenger') ||
          item.href.includes('my-ads') ||
          item.trackingLabel.includes('searchAlertSettings') ||
          item.href.includes('my-realestate')
        ) {
          modifiedHref = `${baseUrl}${item.href}`;
        } else {
          modifiedHref = `${accountBaseUrl}${item.href}`;
        }

        return { ...item, href: modifiedHref };
      });
    }
    return {
      label: tm('$hg.nav.loggedInMenuItem.label'),
      children: privateItems,
    };
  } else {
    return {
      label: tm('$hg.nav.harmonize.overviewMenuItem.label'),
      href: `${accountBaseUrl}/${locale.value}/overview`,
    };
  }
});

const isFooterHidden = computed((): boolean => {
  return isFunnelPage(route.path);
});

// Head/Meta management
useHead(() => getAppMetaInfo(locale.value as any));

// Methods

const setCurrentPage = (page: Pages) => {
  commonStore.setCurrentPage(page);
};

const setLoggedInUser = (username: string) => {
  commonStore.setLoggedInUser(username);
};

const resetUser = () => {
  commonStore.resetUser();
};

const updateUserDetails = async () => {
  if (await checkUserAuthenticated()) {
    isLoggedIn.value = true;
    setLoggedInUser(auth0?.user.value?.['cognito:username'] || '');
  } else {
    isLoggedIn.value = false;
    resetUser();
  }
};

const handleFavouritesClick = () => {
  handleTrackHeaderClick({
    eventAction: 'open_favourites',
    eventLabel: 'tab_navigation',
    contentLanguage: locale.value,
  });
};

const shouldHideMyTenantPlusMenuItem = computed(() => {
  const storedParams = getAndroidWebViewParamsFromStorage();
  const source = (route.query?.source as string) || storedParams.source;
  const tenantPlusCh = (route.query?.tenantPlusCh as string) || storedParams.tenantPlusCh;

  return source === ANDROID_WEBVIEW_SOURCE && tenantPlusCh !== 'true';
});

// Watchers
watch(
  () => route.path,
  (path: string) => {
    const currentPage = getPage(path);
    if (currentPage) {
      setCurrentPage(currentPage);
    }
  },
);

// Lifecycle hooks
onMounted(async () => {
  if (source || tenantPlusCh) {
    setAndroidWebViewParamsInStorage(source, tenantPlusCh);
  }
  await updateUserDetails();
});

onServerPrefetch(() => {
  const currentPage = getPage(route.path);
  if (currentPage) {
    setCurrentPage(currentPage);
  }
});
</script>

<template>
  <div id="app">
    <HgLayout
      :baseUrl="baseUrl"
      :currentLanguage="$i18n.locale"
      :currentRoute="$route"
      :getLanguageToLink="getLanguageSwitchLink"
      :hideFooter="isFooterHidden"
      :isTopNavEnabled="isNewNavigationEnabled"
      :isUserLoggedIn="isLoggedIn"
      @trackFooterClick="handleTrackFooterClick"
      @trackHeaderClick="handleTrackHeaderClick"
    >
      <template #headerMetaAction>
        <HgMainNavItem
          :menuItem="adMenuItem"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          :bordered="true"
          @click="
            handleTrackHeaderClick({
              eventAction: 'open_insertAd',
              eventLabel: 'tab_navigation',
              contentLanguage: $i18n.locale,
            })
          "
        />
      </template>
      <template #headerMetaNavigation>
        <FavouritesLink
          :menuItem="favouritesMenuItem"
          :menuNavItem="HgMainNavItem"
          :route="route"
          :baseUrl="baseUrl"
          @click="handleFavouritesClick"
        />
        <HgMainNavItem
          :menuItem="mainHeaderMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          dropdownOrientation="right"
          :currentRoute="$route"
          @mainNavItemClick="handleMainNavTracking"
          @dropDownMenuItemClick="handleMainNavTracking"
        />
      </template>
      <template #sideMenuMetaNavigation>
        <FavouritesLink
          :menuItem="favouritesMenuItem"
          :menuNavItem="HgSideMenuNavItem"
          :route="route"
          :baseUrl="baseUrl"
          @click="handleFavouritesClick"
        />
        <template v-if="!isLoggedIn">
          <HgSideMenuNavItem
            :menuItem="userAccountMenuItem"
            icon="PersonOutline"
            :baseUrl="baseUrl"
            :currentRoute="$route"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
      </template>
      <template #sideMenuAccountNavigation>
        <template v-if="isLoggedIn">
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.overviewMenuItem') as MenuItem"
            icon="PersonOutline"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.settingsMenuItem') as MenuItem"
            icon="Settings"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.myAdsMenuItem') as MenuItem"
            icon="Appointment"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.myRealEstateMenuItem') as MenuItem"
            icon="House"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.searchAlertMenuItem') as MenuItem"
            icon="Bell"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            v-if="!shouldHideMyTenantPlusMenuItem"
            :menuItem="$tm('$hg.nav.harmonize.tenantPlusMenuItem') as MenuItem"
            icon="PlusMembership"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.signOutMenuItem') as MenuItem"
            icon="SignOut"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
      </template>
      <input id="utm_source" type="hidden" />
      <input id="utm_medium" type="hidden" />
      <input id="utm_campaign" type="hidden" />
      <router-view />
    </HgLayout>
  </div>
</template>

<style lang="scss">
@use '@/assets/globals-hg' as *;

#side-menu-language-switch a {
  &.router-link-exact-active {
    border-bottom: 0;
  }
}
</style>
